/**
 * Cart Drawer
 * Smart cart with rules engine integration
 */

import type { ShopifyCart, ShopifyLineItem } from '../../types/shopify';
import type { CartDrawerSettings } from '../../types/settings';
import { SettingsParser } from '../../types/settings';
import { eventBus } from '../../types/events';
import { ShopifyAPI } from '../../utils/shopify-api';
import {
  qs,
  qsa,
  addClass,
  removeClass,
  toggleClass,
  trapFocus,
  formatMoney,
  debounce,
} from '../../utils/dom';
import {
  RuleEngine,
  GiftWithPurchaseRule,
  FreeShippingRule,
  BundleRecommendationRule,
  type BundleConfig,
} from './rules';

export class CartDrawer {
  private settings: SettingsParser<CartDrawerSettings>;
  private cart: ShopifyCart | null = null;
  private ruleEngine = new RuleEngine();
  private isOpen = false;
  private focusTrap: (() => void) | null = null;

  constructor(private container: HTMLElement) {
    this.settings = new SettingsParser(container);
    this.init();
  }

  private async init(): Promise<void> {
    // Setup rules
    this.setupRules();

    // Load cart
    await this.loadCart();

    // Setup event listeners
    this.setupEventListeners();

    // Listen for global cart events
    this.setupGlobalListeners();
  }

  private setupRules(): void {
    // Gift with purchase
    if (this.settings.get('enable_gift_threshold', false)) {
      const threshold = this.settings.get('gift_threshold_amount', 100);
      const giftHandle = this.settings.get('gift_product_handle');

      if (giftHandle) {
        // Note: Would need to fetch gift variant ID
        // Simplified for demo
        const giftRule = new GiftWithPurchaseRule(threshold, 0);
        this.ruleEngine.addRule(giftRule);
      }
    }

    // Free shipping bar
    if (this.settings.get('enable_free_shipping_bar', true)) {
      const threshold = this.settings.get('free_shipping_threshold', 75);
      const shippingRule = new FreeShippingRule(threshold);
      this.ruleEngine.addRule(shippingRule);
    }

    // Bundle hints
    if (this.settings.get('enable_bundle_hints', false)) {
      const bundleRulesJSON = this.settings.get('bundle_rules');
      if (bundleRulesJSON) {
        try {
          const bundles = JSON.parse(bundleRulesJSON) as BundleConfig[];
          const bundleRule = new BundleRecommendationRule(bundles);
          this.ruleEngine.addRule(bundleRule);
        } catch (error) {
          console.error('Failed to parse bundle rules:', error);
        }
      }
    }
  }

  private setupEventListeners(): void {
    // Close button
    const closeBtn = qs('[data-drawer-close]', this.container);
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.close());
    }

    // Overlay click
    const overlay = qs('[data-drawer-overlay]', this.container);
    if (overlay) {
      overlay.addEventListener('click', () => this.close());
    }

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Checkout button
    const checkoutBtn = qs('[data-checkout]', this.container);
    if (checkoutBtn) {
      checkoutBtn.addEventListener('click', () => {
        window.location.href = '/checkout';
      });
    }
  }

  private setupGlobalListeners(): void {
    // Listen for cart add events
    eventBus.on('cart:add', async (event) => {
      await this.loadCart();
      this.open('cart');
    });

    // Listen for cart update events
    eventBus.on('cart:update', async () => {
      await this.loadCart();
    });

    // Listen for drawer open events
    eventBus.on('drawer:open', () => {
      this.open('manual');
    });

    // Cart icon clicks
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const cartTrigger = target.closest('[data-cart-trigger]');

      if (cartTrigger) {
        e.preventDefault();
        this.open('cart');
      }
    });
  }

  private async loadCart(): Promise<void> {
    try {
      this.cart = await ShopifyAPI.getCart();
      await this.render();
      this.updateCartCount();

      // Evaluate rules
      if (this.cart) {
        await this.evaluateRules();
      }
    } catch (error) {
      console.error('Failed to load cart:', error);
      eventBus.dispatch('cart:error', {
        message: 'Failed to load cart',
        error: error instanceof Error ? error : undefined,
      });
    }
  }

  private async evaluateRules(): Promise<void> {
    if (!this.cart) return;

    const results = await this.ruleEngine.evaluateAll({
      cart: this.cart,
      settings: this.settings.getAll(),
    });

    // Render rule messages
    this.renderRuleMessages(results);

    // Update free shipping bar
    const shippingRule = this.ruleEngine
      .getRules()
      .find((r) => r.id === 'free-shipping') as FreeShippingRule | undefined;

    if (shippingRule) {
      this.updateShippingBar(shippingRule.getProgress(this.cart));
    }
  }

  private renderRuleMessages(results: Map<string, any>): void {
    const messagesContainer = qs('[data-rule-messages]', this.container);
    if (!messagesContainer) return;

    messagesContainer.innerHTML = '';

    results.forEach((result, ruleId) => {
      if (result.message) {
        const messageEl = document.createElement('div');
        messageEl.className = `rule-message rule-message--${ruleId} ${
          result.shouldApply ? 'active' : 'inactive'
        }`;
        messageEl.textContent = result.message;
        messagesContainer.appendChild(messageEl);
      }
    });
  }

  private updateShippingBar(progress: number): void {
    const progressBar = qs<HTMLElement>('[data-shipping-progress]', this.container);
    const progressFill = qs<HTMLElement>(
      '[data-shipping-progress-fill]',
      this.container
    );

    if (progressBar && progressFill) {
      progressFill.style.width = `${progress}%`;

      if (progress >= 100) {
        addClass(progressBar, 'complete');
      } else {
        removeClass(progressBar, 'complete');
      }
    }
  }

  private async render(): Promise<void> {
    if (!this.cart) return;

    const itemsContainer = qs('[data-cart-items]', this.container);
    const emptyState = qs('[data-cart-empty]', this.container);
    const totalEl = qs('[data-cart-total]', this.container);

    if (!itemsContainer) return;

    // Show/hide empty state
    if (this.cart.items.length === 0) {
      if (emptyState) removeClass(emptyState, 'hidden');
      if (itemsContainer) addClass(itemsContainer, 'hidden');
      return;
    } else {
      if (emptyState) addClass(emptyState, 'hidden');
      if (itemsContainer) removeClass(itemsContainer, 'hidden');
    }

    // Render items
    itemsContainer.innerHTML = this.cart.items
      .map((item, index) => this.renderLineItem(item, index + 1))
      .join('');

    // Update total
    if (totalEl) {
      totalEl.textContent = formatMoney(this.cart.total_price);
    }

    // Setup line item listeners
    this.setupLineItemListeners();
  }

  private renderLineItem(item: ShopifyLineItem, line: number): string {
    const price = formatMoney(item.price);
    const linePrice = formatMoney(item.line_price);

    return `
      <div class="cart-drawer-item" data-line="${line}">
        <div class="cart-drawer-item__image">
          ${
            item.image
              ? `<img src="${item.image}" alt="${item.title}" loading="lazy">`
              : '<div class="placeholder-image"></div>'
          }
        </div>
        <div class="cart-drawer-item__details">
          <h3 class="cart-drawer-item__title">${item.product_title}</h3>
          ${
            item.variant_title
              ? `<p class="cart-drawer-item__variant">${item.variant_title}</p>`
              : ''
          }
          <p class="cart-drawer-item__price">${price}</p>
        </div>
        <div class="cart-drawer-item__quantity">
          <button 
            class="quantity-btn" 
            data-quantity-change="${line}" 
            data-change="-1"
            aria-label="Decrease quantity">
            −
          </button>
          <input 
            type="number" 
            value="${item.quantity}" 
            min="0"
            data-quantity-input="${line}"
            aria-label="Quantity">
          <button 
            class="quantity-btn" 
            data-quantity-change="${line}" 
            data-change="1"
            aria-label="Increase quantity">
            +
          </button>
        </div>
        <div class="cart-drawer-item__total">
          <p>${linePrice}</p>
          <button 
            class="cart-drawer-item__remove" 
            data-remove-item="${line}"
            aria-label="Remove item">
            ×
          </button>
        </div>
      </div>
    `;
  }

  private setupLineItemListeners(): void {
    // Quantity change buttons
    const changeButtons = qsa('[data-quantity-change]', this.container);
    changeButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const line = parseInt(btn.dataset.quantityChange || '0', 10);
        const change = parseInt(btn.dataset.change || '0', 10);
        this.updateQuantity(line, change);
      });
    });

    // Quantity inputs
    const quantityInputs = qsa<HTMLInputElement>(
      '[data-quantity-input]',
      this.container
    );
    quantityInputs.forEach((input) => {
      input.addEventListener(
        'change',
        debounce(() => {
          const line = parseInt(input.dataset.quantityInput || '0', 10);
          const newQuantity = parseInt(input.value, 10);
          this.setQuantity(line, newQuantity);
        }, 300)
      );
    });

    // Remove buttons
    const removeButtons = qsa('[data-remove-item]', this.container);
    removeButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const line = parseInt(btn.dataset.removeItem || '0', 10);
        this.removeItem(line);
      });
    });
  }

  private async updateQuantity(line: number, change: number): Promise<void> {
    if (!this.cart) return;

    const item = this.cart.items[line - 1];
    if (!item) return;

    const newQuantity = Math.max(0, item.quantity + change);
    await this.setQuantity(line, newQuantity);
  }

  private async setQuantity(line: number, quantity: number): Promise<void> {
    try {
      this.cart = await ShopifyAPI.changeLineItem(line, quantity);
      await this.render();
      await this.evaluateRules();
      this.updateCartCount();

      eventBus.dispatch('cart:update', { cart: this.cart });
    } catch (error) {
      console.error('Failed to update quantity:', error);
      eventBus.dispatch('cart:error', {
        message: 'Failed to update quantity',
        error: error instanceof Error ? error : undefined,
      });
    }
  }

  private async removeItem(line: number): Promise<void> {
    await this.setQuantity(line, 0);
  }

  private updateCartCount(): void {
    const countElements = qsa('[data-cart-count]');
    const count = this.cart?.item_count || 0;

    countElements.forEach((el) => {
      el.textContent = count.toString();

      if (count > 0) {
        removeClass(el, 'hidden');
      } else {
        addClass(el, 'hidden');
      }
    });
  }

  public open(source: 'cart' | 'notification' | 'manual' = 'manual'): void {
    if (this.isOpen) return;

    removeClass(this.container, 'hidden');
    addClass(this.container, 'active');

    // Add body class to prevent scrolling
    document.body.style.overflow = 'hidden';

    // Setup focus trap
    setTimeout(() => {
      this.focusTrap = trapFocus(this.container);
    }, 100);

    this.isOpen = true;

    eventBus.dispatch('drawer:open', { source });
  }

  public close(): void {
    if (!this.isOpen) return;

    removeClass(this.container, 'active');
    setTimeout(() => {
      addClass(this.container, 'hidden');
    }, 300);

    // Remove body class
    document.body.style.overflow = '';

    // Remove focus trap
    if (this.focusTrap) {
      this.focusTrap();
      this.focusTrap = null;
    }

    this.isOpen = false;

    eventBus.dispatch('drawer:close', {});
  }

  /**
   * Public API: Add item to cart
   */
  public async addItem(variantId: number, quantity = 1): Promise<void> {
    try {
      this.cart = await ShopifyAPI.addToCart({
        items: [{ id: variantId, quantity }],
      });

      await this.render();
      await this.evaluateRules();
      this.updateCartCount();

      if (this.cart.items.length > 0) {
        eventBus.dispatch('cart:add', {
          item: this.cart.items[this.cart.items.length - 1],
          cart: this.cart,
        });
      }
    } catch (error) {
      console.error('Failed to add to cart:', error);
      eventBus.dispatch('cart:error', {
        message: error instanceof Error ? error.message : 'Failed to add to cart',
        error: error instanceof Error ? error : undefined,
      });
    }
  }
}

// Auto-initialize
document.addEventListener('DOMContentLoaded', () => {
  const drawer = qs<HTMLElement>('[data-cart-drawer]');
  if (drawer) {
    const instance = new CartDrawer(drawer);

    // Expose globally for easy access
    (window as any).cartDrawer = instance;
  }
});
